import React, { Component, Suspense } from 'react';
import { cloneDeep, compact, pick } from 'lodash';
import { compose } from 'recompose';
import { MutationFunction, MutationResult } from 'react-apollo';
import { InMemoryCache } from 'apollo-cache-inmemory';
import { Form, Formik } from 'formik';

import { ManualPostComponent, ContentInput, Network, PostType } from 'api';
import { UserGroup } from 'types/app';
import { IWithContent, IWithQueryParams, withContent, withQueryParams } from 'providers';
import { contentCollection, contentModel } from 'apollo/reducers';
import { USER_FEED_ID } from 'constants/index';

import { formatBody, formatCommentary, isApprovedGroup } from '../helpers';
import { PostSchema } from '../validations';
import { PostForm } from '../types';

interface IPropsEnhanced extends PostForm, IWithContent, IWithQueryParams {}

const enhancers = compose<IPropsEnhanced, {}>(withContent, withQueryParams);

class EditPostForm extends Component<IPropsEnhanced> {
  isTimeline(group: UserGroup) {
    return group.id === USER_FEED_ID;
  }

  createCommentary() {
    const { content } = this.props;

    const emptyTwitter = { network: Network.Twitter, commentary: '' };
    const emptyLinkedIn = { network: Network.Linkedin, commentary: '' };
    const emptyFacebook = { network: Network.Facebook, commentary: '' };

    if (!content.commentary) {
      return [emptyTwitter, emptyLinkedIn, emptyFacebook];
    }

    function getCommentary(network: Network) {
      return content.commentary.find(commentary => commentary.network === network);
    }

    return [
      getCommentary(Network.Twitter) || emptyTwitter,
      getCommentary(Network.Linkedin) || emptyLinkedIn,
      getCommentary(Network.Facebook) || emptyFacebook,
    ];
  }

  getValues() {
    const { content } = this.props;

    // Needed for editing
    const contentInput = pick(
      content,
      'approval',
      'content_id',
      'content_source_id',
      'group_ids',
      'is_important',
      'is_shareable',
      'pinned_end_at',
      'user',
      'url_param_values',
      'content_object.url',
      'content_object.photos',
      'content_object.post_type',
      'content_object.url_metadata',
      'content_object.tweet_metadata',
      'content_object.youtube_metadata',
      'content_object.instagram_metadata',
      'content_object.reddit_metadata',
    );

    // Modifiers
    contentInput.is_pinned = content.is_pinned || false;
    contentInput.content_object.body = content.content_object.body || '';
    contentInput.commentary = this.createCommentary();
    contentInput.url_param_values = content.url_param_values || [];

    return contentInput as ContentInput;
  }

  editPost = (cache: InMemoryCache, result: MutationResult) => {
    const { content, query, variables, handleCompleted, selectedGroup } = this.props;

    const { data } = result;
    const { processContent } = data;
    const shouldRemove =
      selectedGroup &&
      !this.isTimeline(selectedGroup) &&
      !isApprovedGroup(processContent, selectedGroup);

    contentModel({ contentId: content.content_id, cache, result, query, variables });
    handleCompleted('posting:modified', true);

    // Ensure post wasn't removed from current group
    if (shouldRemove) {
      contentCollection({
        name: 'removePost',
        cache,
        result,
        query,
        variables,
        contentId: content.content_id,
      });
    }
  };

  handleSubmit = (processContent: MutationFunction) => (content: ContentInput) => {
    const contentToSubmit = cloneDeep(content);

    // Cleanse values object to match ContentInput
    const contentInput = pick(
      contentToSubmit,
      'content_id',
      'content_source_id',
      'is_important',
      'is_shareable',
      'is_pinned',
      'pinned_end_at',
      'content_object.url',
      'content_object.photos',
      'content_object.post_type',
      'url_param_values',
    );

    // Modifiers
    contentInput.group_ids = compact(contentToSubmit.group_ids);
    contentInput.commentary = formatCommentary(contentToSubmit);
    contentInput.content_object.body = formatBody(contentToSubmit);
    if (!contentInput.content_object.url) {
      contentInput.content_object.post_type = PostType.Manual;
    }

    const options = {
      update: this.editPost,
      variables: { content: contentInput },
    };

    this.props.toggleWork();

    return processContent(options);
  };

  render() {
    const { children, handleError, isChanging, renderEditor } = this.props;

    return (
      <ManualPostComponent onError={handleError}>
        {processContent => (
          <Formik
            enableReinitialize
            onSubmit={this.handleSubmit(processContent)}
            initialValues={this.getValues()}
            validationSchema={PostSchema()}>
            {props => (
              <Form>
                {isChanging ? (
                  <Suspense fallback={children}>{renderEditor(props)}</Suspense>
                ) : (
                  children
                )}
              </Form>
            )}
          </Formik>
        )}
      </ManualPostComponent>
    );
  }
}

export default enhancers(EditPostForm);
